import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { createTagDefinition } from "../../../api/tagDefinitions";
import { TWclassNames } from "../../utils/Div";

const NEW_TAG_PALETTE = ["#0d9488", "#db2777", "#65a30d", "#0284c7", "#7c3aed"];
const FALLBACK_COLOR = "#94a3b8";
const PANEL_GAP = 6;
const VIEWPORT_MARGIN = 12;
const MIN_PANEL_HEIGHT = 140;

function isHexColor(color) {
  return typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color.trim());
}

function dotColor(tag) {
  return isHexColor(tag?.color) ? tag.color.trim() : FALLBACK_COLOR;
}

function chipTint(tag) {
  return isHexColor(tag?.color) ? `${tag.color.trim()}1a` : "#f1f5f9";
}

/**
 * Chip + search combobox for tag_definitions (design: «Etikettvelger»).
 * Selected labels render as removable chips; the panel supports search, keyboard
 * navigation and creating a new label from the query.
 */
export function CaseTagCombobox({
  tags,
  loading,
  selectedNorm,
  tagIdsMatch,
  toggleTag,
  clearAll,
  onTagCreated,
  tagLabel,
  label = null,
  labelClassName = "",
  scopeModule = "case",
  allowCreate = true,
  maxHeight = 360,
}) {
  const { t } = useTranslation();
  const listboxId = useId();
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [createError, setCreateError] = useState(null);
  const [placement, setPlacement] = useState({ top: 0, left: 0, width: 280, maxH: maxHeight, flip: false });

  const isSelected = useCallback(
    (tag) => selectedNorm.some((id) => tagIdsMatch(id, tag.id)),
    [selectedNorm, tagIdsMatch]
  );

  const selectedTags = useMemo(
    () =>
      selectedNorm
        .map((id) => tags.find((tag) => tagIdsMatch(id, tag.id)))
        .filter(Boolean),
    [selectedNorm, tags, tagIdsMatch]
  );

  const q = query.trim();
  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    if (!needle) return tags;
    return tags.filter((tag) => tagLabel(tag).toLowerCase().includes(needle));
  }, [tags, q, tagLabel]);

  const showCreate =
    allowCreate &&
    !loading &&
    !!q &&
    !tags.some((tag) => tagLabel(tag).toLowerCase() === q.toLowerCase());
  const total = filtered.length + (showCreate ? 1 : 0);

  useEffect(() => {
    if (hi > Math.max(0, total - 1)) setHi(Math.max(0, total - 1));
  }, [hi, total]);

  const place = useCallback(() => {
    const wrap = wrapRef.current;
    const trigger = triggerRef.current;
    if (!wrap || !trigger) return;
    const wrapRect = wrap.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const below = window.innerHeight - triggerRect.bottom - VIEWPORT_MARGIN - PANEL_GAP;
    const above = triggerRect.top - VIEWPORT_MARGIN - PANEL_GAP;
    const flip = below < Math.min(maxHeight, 240) && above > below;
    const maxH = Math.max(MIN_PANEL_HEIGHT, Math.min(maxHeight, flip ? above : below));
    const width = Math.min(Math.max(wrapRect.width, 260), window.innerWidth - 16);
    const left = Math.max(8, Math.min(wrapRect.left, window.innerWidth - width - 8));
    const top = flip ? triggerRect.top - PANEL_GAP : triggerRect.bottom + PANEL_GAP;
    setPlacement({ top, left, width, maxH, flip });
  }, [maxHeight]);

  const openPanel = () => {
    setCreateError(null);
    setQuery("");
    setHi(0);
    setOpen(true);
  };

  const closePanel = useCallback(({ focusTrigger = false } = {}) => {
    setOpen(false);
    setQuery("");
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    // Scrolling inside the panel's own list does not move the trigger.
    const onScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      place();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, place]);

  // Chips can wrap onto a new line when labels are added/removed; keep the panel attached.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, selectedNorm.length, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      closePanel();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, closePanel]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${hi}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, hi]);

  const createLabel = async () => {
    if (!q || creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const color = NEW_TAG_PALETTE[tags.length % NEW_TAG_PALETTE.length];
      const created = await createTagDefinition({ name: q, color, scope_module: scopeModule });
      if (!created?.id) {
        const msg = Array.isArray(created?.errors) ? created.errors.join(", ") : null;
        setCreateError(msg || t("case.labels.create_failed"));
        return;
      }
      onTagCreated(created);
      setQuery("");
      setHi(0);
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => Math.min(Math.max(0, total - 1), h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hi < filtered.length) toggleTag(filtered[hi]);
      else if (showCreate) createLabel();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closePanel({ focusTrigger: true });
    } else if (e.key === "Tab") {
      closePanel();
    } else if (e.key === "Backspace" && !query && selectedTags.length) {
      toggleTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const countText = selectedTags.length
    ? t("case.labels.selected_count", { count: selectedTags.length })
    : t("case.labels.none_selected");
  const activeId = total > 0 ? `${listboxId}-opt-${hi}` : undefined;

  const panel = (
    <div
      ref={panelRef}
      className="fixed z-[200] flex flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-[0_12px_32px_-8px_rgba(15,23,42,0.18),0_2px_6px_rgba(15,23,42,0.06)]"
      style={{
        left: placement.left,
        width: placement.width,
        maxHeight: placement.maxH,
        ...(placement.flip
          ? { top: placement.top, transform: "translateY(-100%)" }
          : { top: placement.top }),
      }}
    >
      <div className="flex-none border-b border-[#eef1f5] p-2">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setCreateError(null);
            setHi(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={
            allowCreate ? t("case.labels.search_or_create") : t("case.labels.search")
          }
          className="block h-[34px] w-full rounded-[7px] border border-[#d7dde6] px-2.5 text-[14px] text-slate-800 shadow-none outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-[3px] focus:ring-indigo-500/15"
        />
      </div>
      <div
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
      >
        {loading ? (
          <div className="px-2.5 py-3.5 text-[13px] text-slate-400">{t("Loading...")}</div>
        ) : (
          <>
            {filtered.map((tag, i) => {
              const on = isSelected(tag);
              return (
                <div
                  key={tag.id}
                  id={`${listboxId}-opt-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={on}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleTag(tag)}
                  onMouseEnter={() => hi !== i && setHi(i)}
                  className={TWclassNames(
                    "flex min-h-[34px] cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-slate-800",
                    i === hi ? "bg-slate-100" : on ? "bg-slate-50" : "bg-transparent"
                  )}
                >
                  <span className="h-[9px] w-[9px] flex-none rounded-full" style={{ backgroundColor: dotColor(tag) }} />
                  <span className="min-w-0 flex-1 truncate">{tagLabel(tag)}</span>
                  <span className="w-4 flex-none text-center text-[13px] font-semibold text-indigo-600" aria-hidden="true">
                    {on ? "✓" : ""}
                  </span>
                </div>
              );
            })}
            {showCreate ? (
              <div
                id={`${listboxId}-opt-${filtered.length}`}
                data-index={filtered.length}
                role="option"
                aria-selected={false}
                aria-disabled={creating}
                onMouseDown={(e) => e.preventDefault()}
                onClick={createLabel}
                onMouseEnter={() => setHi(filtered.length)}
                className={TWclassNames(
                  "flex min-h-[34px] items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] text-indigo-600",
                  creating ? "cursor-wait opacity-60" : "cursor-pointer",
                  hi === filtered.length ? "bg-indigo-50" : "bg-transparent"
                )}
              >
                <span className="w-[9px] text-center font-semibold">+</span>
                <span className="min-w-0 flex-1 truncate">{t("case.labels.create", { name: q })}</span>
              </div>
            ) : null}
            {createError ? (
              <div role="alert" className="px-2.5 py-1.5 text-[12.5px] text-red-600">
                {createError}
              </div>
            ) : null}
            {total === 0 ? (
              <div className="px-2.5 py-3.5 text-[13px] text-slate-400">
                {tags.length === 0 && !q ? t("No labels defined") : t("case.labels.no_matches")}
              </div>
            ) : null}
          </>
        )}
      </div>
      <div className="flex flex-none items-center justify-between gap-2 border-t border-[#eef1f5] px-3 py-[7px] text-[11.5px] text-slate-400">
        <span>{countText}</span>
        <span className="hidden sm:inline">{t("case.labels.keyboard_hint")}</span>
      </div>
    </div>
  );

  return (
    <div ref={wrapRef} className="flex flex-col gap-2">
      {label || selectedTags.length > 0 ? (
        <div className="flex items-center justify-between">
          {label ? <span className={TWclassNames(labelClassName, "!mb-0")}>{label}</span> : <span />}
          {selectedTags.length > 0 ? (
            <button
              type="button"
              onClick={clearAll}
              className="border-0 bg-transparent p-0 text-[12px] text-slate-500 hover:text-slate-800"
            >
              {t("case.labels.clear_all")}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex h-[26px] max-w-full items-center gap-1.5 rounded-full pl-[9px] pr-1 text-[12.5px] font-medium text-slate-800"
            style={{ backgroundColor: chipTint(tag) }}
          >
            <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ backgroundColor: dotColor(tag) }} />
            <span className="truncate">{tagLabel(tag)}</span>
            <button
              type="button"
              onClick={() => toggleTag(tag)}
              aria-label={t("case.labels.remove", { name: tagLabel(tag) })}
              className="h-[18px] w-[18px] flex-none rounded-full border-0 bg-transparent p-0 text-[14px] leading-[18px] text-inherit opacity-70 hover:bg-black/[0.06] hover:opacity-100"
            >
              ×
            </button>
          </span>
        ))}
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => (open ? closePanel() : openPanel())}
          className="inline-flex h-[26px] flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed border-[#cbd3de] bg-white px-2.5 text-[12.5px] font-medium text-slate-500 hover:border-slate-400 hover:text-slate-800"
        >
          <span className="text-[15px] leading-none" aria-hidden="true">
            +
          </span>
          {selectedTags.length ? t("case.labels.add") : t("case.labels.add_label")}
        </button>
      </div>
      {open && typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </div>
  );
}
